import { useHistory, useLocation } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

export type ParamType = 'string' | 'number' | 'boolean' | 'date' | 'array';

export type ParamFilterBase<T extends ParamType> = {
  field: string;
  type: T;
  optional?: boolean;
};

export type ParamFilterString = ParamFilterBase<'string'> & {
  values?: readonly string[];
};

export type ParamFilterArray = ParamFilterBase<'array'>;

export type ParamFilter =
  | ParamFilterString
  | ParamFilterBase<'number'>
  | ParamFilterBase<'boolean'>
  | ParamFilterBase<'date'>
  | ParamFilterArray;

/**
 * Maps parameter types to their corresponding TypeScript types.
 * Used to provide type safety when working with URL parameters.
 */
type ParamTypeMap = {
  string: string;
  number: number;
  boolean: boolean;
  date: Date;
  array: string[];
};

/**
 * Transforms an array of parameter filters typed ParamFilter[] into a type-safe object type.
 * Fields can be marked as optional using the optional flag.
 *
 * @example
 * ```typescript
 * const possibleParams = [
 *   { field: 'status', type: 'string', values: ['active', 'pending'] as const },
 *   { field: 'search', type: 'string', optional: true },
 *   { field: 'age', type: 'number' }
 * ] as const;
 *
 * const filters = useParamFilter(possibleParams);
 * // filters has type:
 * // {
 * //   status: 'active' | 'pending';  // Required field with allowed values
 * //   search?: string;               // Optional field
 * //   age: number;                   // Any number
 * // }
 * ```
 *
 * If you want to add more types support, update the ParamTypeMap.
 *
 * @template P - A readonly array of parameter filters
 * @returns An object type where:
 * - Keys are the filter field names
 * - Values are typed based on the filter type using ParamTypeMap:
 *   - Required fields must be provided
 *   - Optional fields (marked with optional: true) can be omitted
 *   - 'string' filters with values: Union of allowed values from the values array
 *   - Other types: Corresponding type from ParamTypeMap (number, boolean, Date)
 */
type RequiredFields<P extends readonly ParamFilter[]> = {
  [K in P[number] as K extends { optional: true }
    ? never
    : K['field']]: K extends {
    type: 'string';
    values: readonly string[];
  }
    ? K['values'][number]
    : K extends { type: keyof ParamTypeMap }
      ? ParamTypeMap[K['type']]
      : never;
};

type OptionalFields<P extends readonly ParamFilter[]> = {
  [K in P[number] as K extends { optional: true }
    ? K['field']
    : never]?: K extends {
    type: 'string';
    values: readonly string[];
  }
    ? K['values'][number]
    : K extends { type: keyof ParamTypeMap }
      ? ParamTypeMap[K['type']]
      : never;
};

export type ExtractFilterType<P extends readonly ParamFilter[]> =
  RequiredFields<P> & OptionalFields<P>;

export function useParamFilter<P extends readonly ParamFilter[]>(
  possibleParams: P,
) {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const paramFilters = useMemo(() => {
    const filters = {} as ExtractFilterType<P>;

    for (const param of possibleParams) {
      const rawValue = searchParams.get(param.field);
      if (rawValue != null) {
        let parsed: any;
        switch (param.type) {
          case 'array':
            parsed = Array.from(
              new Set(
                rawValue
                  .split(',')
                  .map((item) => item.trim())
                  .filter((item) => item.length > 0),
              ),
            );
            break;
          case 'string':
            if ('values' in param) {
              if (param.values?.includes(rawValue)) {
                parsed = rawValue;
              }
            } else {
              parsed = rawValue;
            }
            break;
          case 'number': {
            const n = Number(rawValue);
            if (!isNaN(n)) parsed = n;
            break;
          }
          case 'boolean':
            if (rawValue === 'true') parsed = true;
            else if (rawValue === 'false') parsed = false;
            break;
          case 'date': {
            const d = new Date(rawValue);
            if (!isNaN(d.getTime())) parsed = d;
            break;
          }
        }

        if (parsed !== undefined) {
          (filters as Record<string, unknown>)[param.field] = parsed;
        }
      }
    }

    return filters;
  }, [location.search, possibleParams]) as ExtractFilterType<P>;

  const setParamFilter = useCallback(
    <K extends keyof ExtractFilterType<P>>(
      key: K,
      value: ExtractFilterType<P>[K] | undefined,
    ) => {
      const newSearchParams = new URLSearchParams(location.search);

      const param = possibleParams.find((p) => p.field === key);
      if (!param) return;

      if (value === undefined) {
        newSearchParams.delete(key.toString());
      } else {
        let strValue: string | null = null;

        switch (param.type) {
          case 'array':
            if (Array.isArray(value)) {
              strValue = value.join(',');
            }
            break;
          case 'string':
            strValue = String(value);
            break;
          case 'number':
            strValue = String(value);
            break;
          case 'boolean':
            strValue = value ? 'true' : 'false';
            break;
          case 'date':
            strValue = (value as Date).toISOString();
            break;
        }

        if (strValue !== null) {
          newSearchParams.set(key.toString(), strValue);
        }
      }

      history.push({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    },
    [history, location.pathname, location.search, possibleParams],
  );

  return { paramFilters, setParamFilter };
}
